import { en } from "./locales/en";
import { es } from "./locales/es";
import type { UIStructure } from "./types";

export type SupportedLanguages = "en" | "es";

export const ui: Record<SupportedLanguages, UIStructure> = {
  es,
  en,
};

export const defaultLang = "en" as const;

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends object
        ? `${K & string}.${NestedKeys<T[K]> & string}`
        : `${K & string}`;
    }[keyof T]
  : never;

type TranslationKeys = NestedKeys<UIStructure>;

type ExtractPlaceholderFields<T extends string> =
  T extends `${string}{{${infer Placeholder}}}${string}` ? Placeholder : never;

type ArgsForKey<Key extends TranslationKeys> =
  Key extends `${infer _Prefix}.${infer Rest}`
    ? ArgsForKey<Rest & TranslationKeys>
    : Key extends `${infer _Prefix}.${infer _Arg}`
      ? { [k in ExtractPlaceholderFields<Key>]: string }
      : Record<string, never>;

type ArgsForKeyOptional<Key extends TranslationKeys> =
  Key extends `${string}{{${string}}}${string}`
    ? ArgsForKey<Key>
    : // eslint-disable-next-line @typescript-eslint/ban-types
      {};

export function useTranslations(lang: SupportedLanguages) {
  return function t<Key extends TranslationKeys>(
    key: Key,
    args?: ArgsForKeyOptional<Key>,
  ): string {
    const getValue = (obj: UIStructure, path: string): string | undefined => {
      return path.split(".").reduce((acc: unknown, part: string): unknown => {
        if (acc && typeof acc === "object" && part in acc) {
          return (acc as Record<string, unknown>)[part];
        }
        return undefined;
      }, obj as unknown) as string | undefined;
    };

    const translation =
      getValue(ui[lang], key) || getValue(ui[defaultLang], key);

    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    if (!args) {
      return translation;
    }

    return Object.entries(args).reduce((str, [argKey, value]) => {
      return str.replace(new RegExp(`{{${argKey}}}`, "g"), String(value));
    }, translation);
  };
}
